import React from 'react';
import { useTranslation } from 'react-i18next';

import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';
import { useSelector } from 'store/hooks';
import { useTheme } from '@mui/material';

import { SelectionTreeT, ScopeOptionsT } from 'business/definitions';
import ShowAllowedAccesses from './ShowAllowedAccesses';
import { getScopeField, translateScopeOptionToSelectionTree } from './helpers';

const SelectAllFilter = ({
    isScopeSelectionActivated,
    scopeOption,
    setSelections,
    tree,
}: {
    isScopeSelectionActivated: boolean;
    scopeOption: ScopeOptionsT;
    setSelections: (selection: SelectionTreeT[]) => void;
    tree: TreeT;
}): JSX.Element => {
    const { t } = useTranslation();
    const theme = useTheme();

    const perimeter = getScopeField(scopeOption.value);
    const userScopes = useSelector((vision) => vision.vision.user.scopes);
    const userScopeForThisPerimeter = userScopes.find(
        (scope) => scope.name === perimeter,
    );
    const filterScope = scopeOption.values;
    const accessTypeForThisPerimeter = userScopeForThisPerimeter?.accessType;

    const shouldDisplayAllowedAccessesList =
        accessTypeForThisPerimeter === 'restricted' && filterScope.length > 0;

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) =>
        event.target.checked
            ? setSelections([translateScopeOptionToSelectionTree(scopeOption)])
            : setSelections([]);

    const label = (
        <Typography variant="body1SemiBold">
            {`${t('treeTableModal.selectAllFilter.globalFilterMessage')}${
                t(`printPage.${perimeter}`).charAt(0).toLowerCase() +
                t(`printPage.${perimeter}`).slice(1)
            }`}
        </Typography>
    );

    return (
        <div
            style={{
                backgroundColor: theme.palette.primary.background,
                borderRadius: '0.2rem',
                padding: '1.5rem',
            }}
        >
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                }}
            >
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={isScopeSelectionActivated}
                            onChange={handleChange}
                        />
                    }
                    label={label}
                />
            </div>
            {accessTypeForThisPerimeter && (
                <div style={{ marginLeft: '1rem', marginTop: '0.5rem' }}>
                    <Typography variant="body1">
                        {t(
                            `treeTableModal.selectAllFilter.accessTypesMapping.${accessTypeForThisPerimeter}`,
                        )}
                    </Typography>
                    {shouldDisplayAllowedAccessesList && (
                        <ShowAllowedAccesses
                            filterScope={filterScope}
                            tree={tree}
                        />
                    )}
                </div>
            )}
        </div>
    );
};

export default SelectAllFilter;
